import { createTheme } from '@mui/material/styles';
import {
  PRIMARY,
  SHADOW_PRIMARY,
  SHADOW_PRIMARY_HOVER,
  LOGIN_BTN_BORDER,
  LOGIN_BTN_BORDER_HOVER,
  LOGIN_BTN_BORDER_DARK,
  LOGIN_BTN_BORDER_DARK_HOVER,
  LOGIN_BTN_SHADOW_HOVER,
  SECONDARY_BUTTON_PRIMARY_GLOW,
} from '../utils/colors';

const fontStack =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Consistent scale: readable body, calmer headings, clearer hierarchy. */
const sharedTypography = {
  fontFamily: fontStack,
  fontSize: 15,
  htmlFontSize: 16,
  h1: {
    fontSize: '2rem',
    fontWeight: 700,
    letterSpacing: '-0.025em',
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '1.625rem',
    fontWeight: 700,
    letterSpacing: '-0.022em',
    lineHeight: 1.22,
  },
  h3: {
    fontSize: '1.375rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    lineHeight: 1.28,
  },
  h4: {
    fontSize: '1.25rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    lineHeight: 1.3,
  },
  h5: {
    fontSize: '1.125rem',
    fontWeight: 600,
    letterSpacing: '-0.015em',
    lineHeight: 1.33,
  },
  h6: {
    fontSize: '1.0625rem',
    fontWeight: 600,
    letterSpacing: '-0.012em',
    lineHeight: 1.4,
  },
  subtitle1: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '-0.01em',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '-0.005em',
  },
  body1: {
    fontSize: '0.9375rem',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.55,
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
    letterSpacing: '0.01em',
  },
  caption: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
  },
  overline: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    lineHeight: 1.5,
    textTransform: 'uppercase',
  },
};

const sharedComponents = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        fontFamily: fontStack,
        transition: 'background-color 200ms ease, color 200ms ease',
        backgroundColor: 'rgba(0, 0, 0, 0)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        transition:
          'background-color 200ms ease, color 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        transition:
          'background-color 200ms ease, color 200ms ease, border-color 200ms ease',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        transition:
          'background-color 200ms ease, color 200ms ease, border-color 200ms ease',
      },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        textTransform: 'none',
      },
      containedPrimary: {
        backgroundColor: PRIMARY,
        boxShadow: SHADOW_PRIMARY,
        '&:hover': {
          backgroundColor: PRIMARY,
          boxShadow: SHADOW_PRIMARY_HOVER,
        },
      },
      outlinedPrimary: ({ theme }) => ({
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor:
          theme.palette.mode === 'dark' ? LOGIN_BTN_BORDER_DARK : LOGIN_BTN_BORDER,
        '&:hover': {
          backgroundColor: 'transparent',
          borderColor:
            theme.palette.mode === 'dark'
              ? LOGIN_BTN_BORDER_DARK_HOVER
              : LOGIN_BTN_BORDER_HOVER,
          boxShadow:
            theme.palette.mode === 'dark'
              ? SECONDARY_BUTTON_PRIMARY_GLOW
              : LOGIN_BTN_SHADOW_HOVER,
        },
      }),
    },
  },
};

/** Opaque surface for portaled dropdowns (Select, Menu, Popover, Autocomplete) in dark mode.
 *  `palette.background.paper` stays transparent for ambient layout cards; menus must not show through. */
const darkDropdownPaper = {
  backgroundImage: 'none',
  backgroundColor: 'rgba(15, 23, 42, 0.96)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
};

/** Light: page bg clear (ambient via Layout/Login); paper #FFFFFF; primary #2563EB */
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#3B82F6' },
    background: {
      default: 'rgba(0, 0, 0, 0)',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1E293B',
    },
    divider: 'rgba(15, 23, 42, 0.08)',
  },
  typography: sharedTypography,
  components: sharedComponents,
});

/** Dark: ambient canvas; transparent surfaces; text matches login-adjacent tokens */
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#3B82F6' },
    background: {
      default: 'rgba(0, 0, 0, 0)',
      paper: 'rgba(0, 0, 0, 0)',
    },
    text: {
      primary: '#E5E7EB',
      secondary: 'rgba(255, 255, 255, 0.6)',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: sharedTypography,
  components: {
    ...sharedComponents,
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(15, 23, 42, 0.92)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.14)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: darkDropdownPaper,
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: darkDropdownPaper,
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: darkDropdownPaper,
      },
    },
  },
});
